// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-20) — where the highest and lowest *change* sits,
 * for "Mark highest & lowest" while the globe shows a change from a held
 * baseline (Analyze → Baseline). The owner held a frame, chose % change,
 * and found a third pin lower than the "Lowest": the pins had been placed
 * on the dataset's own values underneath (`releaseExtremes.ts`), then read
 * as change. What the globe shows is what they now rank.
 *
 * Each texel of the frame is paired with the baseline at the same texel
 * (the change map refuses another grid, `research.change.otherGrid`) and
 * read by the readers' own rule (`changeOf`, with the % floor the map
 * set), so the pin then reads exactly the number it was ranked by. A
 * period's climatology is read per cell, at the texel the cell samples, as
 * the change map paints it (`computePeriodChangeGrid`); the pin lands on
 * that texel, so it reads that number too. Places with no change (grey on
 * the map: one side without data, a baseline too near zero for a %) never
 * count.
 *
 * Ties go as in `releaseExtremes.ts`: the first in row order, or, for a
 * pin that follows, the one nearest where it stands.
 *
 * Compare (A − B) is not covered: B is read point by point on the graphics
 * card (`CompareReference.valueAt`), too slow over a whole frame. There
 * the caller finds nothing rather than the values underneath.
 *
 * The whole frame is walked, so call this from a press, or for following
 * pins within their budget (`createLocateBudget`), never from a pointer
 * handler.
 */

import type { ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'
import type { LumaSnapshot } from '../glLumaSampler'
import { texelUvToLatLon } from '../datasetProbe'
import { gridKey } from '../research/baseline'
import { valueTable, type ChangeMode } from '../research/changeMap'
import { changeOf, type ChangeViewState } from '../research/changeView'
import { cellOf, decodeMean, decodeSigma, NO_MEAN, sampledTexel, type Climatology } from '../research/climatology'
import { arcDeg, type LatLon } from './followExtremes'
import type { ReleaseExtreme } from './releaseExtremes'

/** The frame on the globe as the Analyze panel reads it (`MapRenderer.analysisFrame`). */
export interface ChangeFrame {
  readonly snapshot: LumaSnapshot
  readonly scale: ColorScale
  readonly options?: DatasetOverlayOptions
}

/** Visits each texel that can be ranked, with its change (finite only). */
type Walk = (visit: (x: number, y: number, change: number) => void) => void

/** A held frame: the change of every (code now, code then) pair, made once per frame scale, baseline, mode and floor. */
let pairCache: { now: ColorScale; base: ColorScale; mode: ChangeMode; floor: number | null; table: Float64Array } | null = null

function pairTable(now: ColorScale, base: ColorScale, mode: ChangeMode, floor: number | null): Float64Array {
  if (pairCache && pairCache.now === now && pairCache.base === base && pairCache.mode === mode && pairCache.floor === floor) return pairCache.table
  const a = valueTable(now)
  const b = valueTable(base)
  const table = new Float64Array(256 * 256)
  for (let i = 0; i < 256; i++) {
    for (let j = 0; j < 256; j++) {
      const v = Number.isFinite(b[j]) ? changeOf(a[i], b[j], mode, floor) : null
      table[i * 256 + j] = v ?? NaN
    }
  }
  pairCache = { now, base, mode, floor, table }
  return table
}

/** A climatology's means and steps by code, decoded once (its codes are 16-bit). */
const climTables = new WeakMap<Climatology, { mean: Float64Array; step: Float64Array }>()

function climTable(clim: Climatology): { mean: Float64Array; step: Float64Array } {
  let t = climTables.get(clim)
  if (!t) {
    const mean = new Float64Array(65536)
    const step = new Float64Array(65536)
    for (let code = 0; code < 65536; code++) {
      mean[code] = decodeMean(clim, code)
      // decodeSigma(…, 256) is the step itself: σ = (σ code / 256) × step.
      step[code] = code === NO_MEAN ? NaN : decodeSigma(clim, code, 256)
    }
    t = { mean, step }
    climTables.set(clim, t)
  }
  return t
}

/** The walk over the frame against the change shown; null when the change cannot be read on this frame. */
function walkOf(frame: ChangeFrame, s: ChangeViewState): Walk | null {
  const { snapshot: { data, width, height }, scale, options } = frame
  const field = s.field
  if (!s.mode || s.other || !field || !width || !height) return null
  if (field.gridKey !== gridKey(width, height, options) || field.width !== width || field.height !== height) return null
  const mode = s.mode
  const floor = s.floor

  const period = field.period
  if (!period) {
    if (field.data.length !== width * height) return null
    const table = pairTable(scale, field.scale, mode, floor)
    const base = field.data
    return (visit) => {
      for (let y = 0; y < height; y++) {
        const row = y * width
        for (let x = 0; x < width; x++) {
          const v = table[data[row + x] * 256 + base[row + x]]
          if (v === v) visit(x, y, v)
        }
      }
    }
  }

  const clim = period.clim
  const group = period.groupNow()
  if (group === null || group < 0 || group >= clim.groups) return null
  const now = valueTable(scale)
  const { mean, step } = climTable(clim)
  const layer = group * clim.cw * clim.ch
  const read = (x: number, y: number, visit: (x: number, y: number, change: number) => void) => {
    const k = (layer + cellOf(clim, x, y)) * 2
    const mq = clim.q[k]
    const sq = clim.q[k + 1]
    const m = mean[mq]
    const sigma = sq === 0 ? NaN : (sq / 256) * step[mq]
    const v = changeOf(now[data[y * width + x]], Number.isFinite(m) ? m : null, mode, floor, Number.isFinite(sigma) ? sigma : null)
    if (v !== null) visit(x, y, v)
  }
  if (clim.factor === 1) {
    return (visit) => {
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) read(x, y, visit)
    }
  }
  // Thinned: one texel per cell, the one its value is read at.
  return (visit) => {
    for (let cy = 0; cy < clim.ch; cy++) {
      for (let cx = 0; cx < clim.cw; cx++) {
        const at = sampledTexel(clim, cx * clim.factor, cy * clim.factor)
        read(at.x, at.y, visit)
      }
    }
  }
}

/**
 * The highest and lowest change on the frame, where it sits, and its
 * value in the change's units (or % / σ); null when the change cannot be
 * read here (no baseline, another grid, Compare) or nothing has one.
 * `allowed`: one byte per texel, 0 where it is not looked for (the sea,
 * under "Land only").
 */
export function findChangeExtremes(
  frame: ChangeFrame,
  s: ChangeViewState,
  near?: { readonly max?: LatLon | null; readonly min?: LatLon | null },
  allowed?: Uint8Array | null,
): { max: ReleaseExtreme; min: ReleaseExtreme } | null {
  const walk = walkOf(frame, s)
  if (!walk) return null
  const { width, height } = frame.snapshot
  const ok = allowed && allowed.length === width * height ? allowed : null
  if (allowed && !ok) return null

  let maxValue = -Infinity
  let minValue = Infinity
  walk((x, y, v) => {
    if (ok && !ok[y * width + x]) return
    if (v > maxValue) maxValue = v
    if (v < minValue) minValue = v
  })
  if (maxValue === -Infinity) return null

  const placeOf = (x: number, y: number): LatLon => texelUvToLatLon({ u: (x + 0.5) / width, v: (y + 0.5) / height }, frame.options)
  // Where each extreme sits: the first in row order, or the tie nearest the pin.
  const pick = (value: number, to: LatLon | null | undefined) => {
    let first: LatLon | null = null
    let best: LatLon | null = null
    let bestD = Infinity
    walk((x, y, v) => {
      if (v !== value || (ok && !ok[y * width + x])) return
      if (!to) {
        if (!first) first = placeOf(x, y)
        return
      }
      const at = placeOf(x, y)
      const d = arcDeg(at, to)
      if (d < bestD) { bestD = d; best = at }
    })
    // The fallback is unreachable: the value was seen in this walk, where allowed.
    return { ...((to ? best : first) ?? { lat: 0, lon: 0 }), value }
  }
  return { max: pick(maxValue, near?.max), min: pick(minValue, near?.min) }
}
